import { lazy, Suspense, useEffect } from 'react';
import { useHashRoute, navigate } from './hooks/useHashRoute';
import { leseLetzteUmgebung, merkeLetzteUmgebung } from './lib/letzteUmgebung';
import { AUSWAHL_ROUTE, officeRoute, parseRoute } from './lib/routen';
import { umgebungAus, useSessionStore } from './stores/sessionStore';
import { LoginScreen } from './features/login/LoginScreen';
import { OfficeView } from './features/office/OfficeView';
import { UmgebungsAuswahl } from './features/umgebung/UmgebungsAuswahl';

// Verwaltung & Editor nur fuer Admins — lazy, damit sie nicht im Haupt-Bundle landen
const UmgebungAdminView = lazy(() =>
  import('./features/admin/UmgebungAdminView').then((m) => ({ default: m.UmgebungAdminView })),
);
const SuperadminView = lazy(() =>
  import('./features/superadmin/SuperadminView').then((m) => ({ default: m.SuperadminView })),
);
const EditorView = lazy(() => import('./features/editor/EditorView').then((m) => ({ default: m.EditorView })));

const laden = <div className="app-loading">Lade…</div>;

/** Wurzel: Session pruefen, dann Login | Auswahl | Buero | Verwaltung | Editor. */
export function App() {
  const { status, user, init } = useSessionStore();
  const route = parseRoute(useHashRoute());

  useEffect(() => {
    void init();
  }, [init]);

  // Weiterleitungen gehoeren in einen Effekt, nicht ins Rendern
  useEffect(() => {
    if (status !== 'loggedIn' || !user) return;
    if (route.kind === 'unbekannt') {
      navigate('/');
      return;
    }
    if (route.kind === 'start') {
      const einzige = user.umgebungen.length === 1 ? user.umgebungen[0] : undefined;
      const letzte = umgebungAus(user, leseLetzteUmgebung() ?? '');
      const ziel = einzige ?? letzte;
      navigate(ziel ? officeRoute(ziel.id) : AUSWAHL_ROUTE);
      return;
    }
    if ('umgebungId' in route && !umgebungAus(user, route.umgebungId)) {
      // Gemerkte oder verlinkte Umgebung ohne Zugang: zur Auswahl, Merker weg
      merkeLetzteUmgebung(null);
      navigate(AUSWAHL_ROUTE);
    }
  }, [status, user, route]);

  if (status === 'loading') return laden;
  if (status === 'loggedOut' || !user) return <LoginScreen />;

  if (route.kind === 'auswahl') return <UmgebungsAuswahl user={user} />;
  if (route.kind === 'superadmin' && user.isSuperadmin) {
    return <Suspense fallback={laden}><SuperadminView /></Suspense>;
  }

  const umgebung = 'umgebungId' in route ? umgebungAus(user, route.umgebungId) : undefined;
  if (!umgebung) return laden; // Weiterleitung laeuft

  const istAdmin = user.isSuperadmin || umgebung.rolle === 'admin';
  if (route.kind === 'admin' && istAdmin) {
    return <Suspense fallback={laden}><UmgebungAdminView umgebung={umgebung} /></Suspense>;
  }
  if (route.kind === 'editor' && istAdmin && umgebung.ansichten !== '3d') {
    return <Suspense fallback={laden}><EditorView mapId={umgebung.mapId} umgebungId={umgebung.id} /></Suspense>;
  }

  merkeLetzteUmgebung(umgebung.id);
  // Pro Benutzer und Umgebung einmal mounten: ein Wechsel baut Socket und Runtime neu
  return <OfficeView key={`${user.id}:${umgebung.id}`} user={user} umgebung={umgebung} />;
}
